import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getCourseBuilderModules, saveModuleContent, GetCourseBuilderModulesOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, BookOpen, Video, Brain, MessageSquare, HelpCircle, Settings, Plus, Pencil, Trash2, GripVertical, AlertTriangle, CheckCircle, ShieldAlert, Eye, Play } from 'lucide-react';
import { toast } from 'sonner';
import VideoUploader from '@/components/training-admin/VideoUploader';

type BuilderData = GetCourseBuilderModulesOutputType;
type Module = BuilderData['modules'][0];
type KeyPoint = Module['keyPoints'][0];
type Scenario = Module['scenarios'][0];
type Question = Module['questions'][0];

const STEP_LABELS: Record<string, { icon: typeof BookOpen; label: string }> = {
  welcome: { icon: BookOpen, label: 'Welcome' },
  watch: { icon: Video, label: 'Watch Video' },
  learn: { icon: Brain, label: 'Learn Key Points' },
  practise: { icon: MessageSquare, label: 'Practise Scenarios' },
  check: { icon: HelpCircle, label: 'Knowledge Check' },
  readiness: { icon: CheckCircle, label: 'Readiness' },
  summary: { icon: CheckCircle, label: 'Summary' },
};
const ALL_STEPS = ['welcome', 'watch', 'learn', 'practise', 'check', 'readiness', 'summary'];

const CATEGORIES = ['Safety', 'Tip', 'Key Fact', 'Warning', 'Escalation', 'Standard'];

export default function CourseBuilder() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, loginWithRedirect } = useAuth();
  const [data, setData] = useState<BuilderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [activeTab, setActiveTab] = useState('steps');

  // Dialogs
  const [kpDialog, setKpDialog] = useState(false);
  const [editKp, setEditKp] = useState<Partial<KeyPoint> & { moduleId?: string }>({});
  const [scDialog, setScDialog] = useState(false);
  const [editSc, setEditSc] = useState<Partial<Scenario> & { moduleId?: string }>({});
  const [qDialog, setQDialog] = useState(false);
  const [editQ, setEditQ] = useState<Partial<Question> & { moduleId?: string }>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string; label: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) loginWithRedirect();
  }, [authLoading, user, loginWithRedirect]);

  const load = useCallback(() => {
    if (!courseId || !user) return;
    setLoading(true);
    getCourseBuilderModules({ courseId }).then(d => {
      setData(d);
      if (!selectedModule && d.modules.length > 0) setSelectedModule(d.modules[0].id);
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [courseId, user, selectedModule]);

  useEffect(() => { load(); }, [load]);

  const mod = data?.modules.find(m => m.id === selectedModule);

  const handleSaveModule = async (updates: Record<string, unknown>) => {
    if (!mod) return;
    setSaving(true);
    await saveModuleContent({ moduleId: mod.id, action: 'updateModule', moduleData: updates as any }).catch(() => toast.error('Save failed'));
    setSaving(false);
    load();
    toast.success('Module updated');
  };

  const handleToggleStep = async (step: string) => {
    if (!mod) return;
    const current = mod.stepsEnabled ? mod.stepsEnabled.split(',').map(s => s.trim()) : ALL_STEPS;
    const next = current.includes(step) ? current.filter(s => s !== step) : [...current, step];
    const ordered = ALL_STEPS.filter(s => next.includes(s));
    await handleSaveModule({ stepsEnabled: ordered.join(',') });
  };

  const handleSaveKP = async () => {
    if (!editKp.title) return;
    setSaving(true);
    await saveModuleContent({
      moduleId: editKp.moduleId || mod?.id || '',
      action: 'upsertKeyPoint',
      keyPoint: { id: editKp.id, title: editKp.title, content: editKp.content, expandableDetail: editKp.expandableDetail, category: editKp.category, sopLink: editKp.sopLink, sortOrder: editKp.sortOrder },
    }).catch(() => toast.error('Save failed'));
    setSaving(false);
    setKpDialog(false);
    load();
    toast.success('Key point saved');
  };

  const handleSaveScenario = async () => {
    if (!editSc.title) return;
    setSaving(true);
    await saveModuleContent({
      moduleId: editSc.moduleId || mod?.id || '',
      action: 'upsertScenario',
      scenario: { id: editSc.id, title: editSc.title, contextDescription: editSc.contextDescription, choicesJson: editSc.choicesJson, sortOrder: editSc.sortOrder },
    }).catch(() => toast.error('Save failed'));
    setSaving(false);
    setScDialog(false);
    load();
    toast.success('Scenario saved');
  };

  const handleSaveQuestion = async () => {
    if (!editQ.questionText) return;
    setSaving(true);
    await saveModuleContent({
      moduleId: editQ.moduleId || mod?.id || '',
      action: 'upsertQuestion',
      question: { id: editQ.id, questionText: editQ.questionText, optionsJson: editQ.optionsJson, correctAnswer: editQ.correctAnswer, explanation: editQ.explanation, isCritical: editQ.isCritical, sortOrder: editQ.sortOrder },
    }).catch(() => toast.error('Save failed'));
    setSaving(false);
    setQDialog(false);
    load();
    toast.success('Question saved');
  };

  const handleDelete = async () => {
    if (!deleteConfirm || !mod) return;
    setSaving(true);
    const actionMap: Record<string, string> = { keyPoint: 'deleteKeyPoint', scenario: 'deleteScenario', question: 'deleteQuestion' };
    await saveModuleContent({ moduleId: mod.id, action: actionMap[deleteConfirm.type] as any, deleteId: deleteConfirm.id }).catch(() => toast.error('Delete failed'));
    setSaving(false);
    setDeleteConfirm(null);
    load();
    toast.success('Deleted');
  };

  if (authLoading || !user) return null;
  if (loading) return <div className="p-6 space-y-4 max-w-5xl mx-auto"><Skeleton className="h-8 w-64" /><Skeleton className="h-12 w-full" /><Skeleton className="h-64" /></div>;
  if (!data) return <div className="p-6 text-center"><ShieldAlert className="w-10 h-10 text-destructive mx-auto mb-2" /><p>Course not found</p></div>;

  const enabledSteps = mod?.stepsEnabled ? mod.stepsEnabled.split(',').map(s => s.trim()) : ALL_STEPS;

  return (
    <div className="max-w-6xl mx-auto pb-8">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border bg-card sticky top-0 z-30">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/train/admin" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">{data.course.title}</h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">{data.course.courseCode}</Badge>
              <Badge className={data.course.status === 'Draft' ? 'bg-amber-500/10 text-amber-600' : 'bg-green-500/10 text-green-600'}>{data.course.status}</Badge>
              <span>{data.modules.length} modules</span>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate(`/train/admin/preview/${courseId}?v2=true`)} className="shrink-0">
            <Play className="w-3.5 h-3.5 mr-1" /> Preview V2
          </Button>
        </div>
        {/* Module selector */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
          {data.modules.map(m => (
            <button key={m.id} onClick={() => setSelectedModule(m.id)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                m.id === selectedModule ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'
              }`}>
              {m.displayOrder}. {m.displayLabel}
            </button>
          ))}
        </div>
      </div>

      {mod && (
        <div className="px-4 pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="steps" className="text-xs">Steps</TabsTrigger>
              <TabsTrigger value="content" className="text-xs">Key Points ({mod.keyPointCount})</TabsTrigger>
              <TabsTrigger value="scenarios" className="text-xs">Scenarios ({mod.scenarioCount})</TabsTrigger>
              <TabsTrigger value="questions" className="text-xs">Questions ({mod.questionCount})</TabsTrigger>
              <TabsTrigger value="video" className="text-xs">Video</TabsTrigger>
            </TabsList>

            {/* Steps Tab */}
            <TabsContent value="steps" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Enabled Steps</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {ALL_STEPS.map(step => {
                    const cfg = STEP_LABELS[step];
                    const Icon = cfg.icon;
                    const enabled = enabledSteps.includes(step);
                    return (
                      <div key={step} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                          <span className={`text-sm ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{cfg.label}</span>
                        </div>
                        <Switch checked={enabled} onCheckedChange={() => handleToggleStep(step)} />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Settings className="w-4 h-4" /> Module Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Learning Outcome</label>
                    <Textarea defaultValue={mod.learningOutcome} className="mt-1 text-sm" rows={2}
                      onBlur={e => { if (e.target.value !== mod.learningOutcome) handleSaveModule({ learningOutcome: e.target.value }); }} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground">Est. Minutes</label>
                      <Input type="number" defaultValue={mod.estimatedMinutes}
                        onBlur={e => { const v = parseInt(e.target.value); if (v !== mod.estimatedMinutes) handleSaveModule({ estimatedMinutes: v }); }} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Video Required %</label>
                      <Input type="number" defaultValue={mod.videoRequiredPercent} min={0} max={100}
                        onBlur={e => { const v = parseInt(e.target.value); if (v !== mod.videoRequiredPercent) handleSaveModule({ videoRequiredPercent: v }); }} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Safety Warnings</label>
                    <Textarea defaultValue={mod.safetyWarnings} rows={2} className="mt-1 text-sm"
                      onBlur={e => { if (e.target.value !== mod.safetyWarnings) handleSaveModule({ safetyWarnings: e.target.value }); }} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Common Mistakes</label>
                    <Textarea defaultValue={mod.commonMistakes} rows={2} className="mt-1 text-sm"
                      onBlur={e => { if (e.target.value !== mod.commonMistakes) handleSaveModule({ commonMistakes: e.target.value }); }} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Key Points Tab */}
            <TabsContent value="content" className="mt-4 space-y-3">
              <Button size="sm" onClick={() => { setEditKp({ moduleId: mod.id, sortOrder: mod.keyPoints.length + 1, category: 'Standard' }); setKpDialog(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add Key Point
              </Button>
              {mod.keyPoints.map(kp => (
                <Card key={kp.id}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <GripVertical className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{kp.title}</span>
                            <Badge variant="outline" className="text-[10px]">{kp.category}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{kp.content}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => { setEditKp({ ...kp, moduleId: mod.id }); setKpDialog(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'keyPoint', id: kp.id, label: kp.title })}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Scenarios Tab */}
            <TabsContent value="scenarios" className="mt-4 space-y-3">
              <Button size="sm" onClick={() => { setEditSc({ moduleId: mod.id, sortOrder: mod.scenarios.length + 1, choicesJson: '[]' }); setScDialog(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add Scenario
              </Button>
              {mod.scenarios.map(sc => {
                let choices: { label: string; isCorrect: boolean }[] = [];
                try { choices = JSON.parse(sc.choicesJson); } catch {}
                return (
                  <Card key={sc.id}>
                    <CardContent className="py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{sc.title}</span>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{sc.contextDescription}</p>
                          <div className="flex gap-1 mt-1">
                            {choices.map((c, i) => (
                              <Badge key={i} variant={c.isCorrect ? 'default' : 'outline'} className="text-[10px]">
                                {c.isCorrect ? '✓' : '✗'} {c.label.slice(0, 30)}…
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => { setEditSc({ ...sc, moduleId: mod.id }); setScDialog(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'scenario', id: sc.id, label: sc.title })}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            {/* Questions Tab */}
            <TabsContent value="questions" className="mt-4 space-y-3">
              <Button size="sm" onClick={() => { setEditQ({ moduleId: mod.id, sortOrder: mod.questions.length + 1, isCritical: false, optionsJson: '[]' }); setQDialog(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add Question
              </Button>
              {mod.questions.map(q => (
                <Card key={q.id}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{q.questionText}</span>
                          {q.isCritical && <Badge variant="destructive" className="text-[10px]">Critical</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Answer: {q.correctAnswer} · {q.explanation?.slice(0, 60)}…</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => { setEditQ({ ...q, moduleId: mod.id }); setQDialog(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'question', id: q.id, label: q.questionText })}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            {/* Video Tab */}
            <TabsContent value="video" className="mt-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Video className="w-4 h-4" /> Training Video</CardTitle></CardHeader>
                <CardContent>
                  <VideoUploader moduleId={mod.id} currentUrl={mod.videoUrl} moduleLabel={mod.displayLabel}
                    onSaved={(url) => { load(); }} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Key Point Dialog */}
      <Dialog open={kpDialog} onOpenChange={setKpDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editKp.id ? 'Edit' : 'New'} Key Point</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={editKp.title || ''} onChange={e => setEditKp({ ...editKp, title: e.target.value })} />
            <Textarea placeholder="Content (summary)" value={editKp.content || ''} onChange={e => setEditKp({ ...editKp, content: e.target.value })} rows={2} />
            <Textarea placeholder="Expandable detail" value={editKp.expandableDetail || ''} onChange={e => setEditKp({ ...editKp, expandableDetail: e.target.value })} rows={3} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={editKp.category || 'Standard'} onValueChange={v => setEditKp({ ...editKp, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="SOP link (optional)" value={editKp.sopLink || ''} onChange={e => setEditKp({ ...editKp, sopLink: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKpDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveKP} disabled={saving || !editKp.title}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scenario Dialog */}
      <Dialog open={scDialog} onOpenChange={setScDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editSc.id ? 'Edit' : 'New'} Scenario</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Scenario title" value={editSc.title || ''} onChange={e => setEditSc({ ...editSc, title: e.target.value })} />
            <Textarea placeholder="Context description" value={editSc.contextDescription || ''} onChange={e => setEditSc({ ...editSc, contextDescription: e.target.value })} rows={3} />
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Choices (JSON)</label>
              <Textarea placeholder='[{"label":"...", "isCorrect": true, "feedback":"..."}]' value={editSc.choicesJson || '[]'}
                onChange={e => setEditSc({ ...editSc, choicesJson: e.target.value })} rows={5} className="font-mono text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveScenario} disabled={saving || !editSc.title}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={qDialog} onOpenChange={setQDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editQ.id ? 'Edit' : 'New'} Question</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea placeholder="Question text" value={editQ.questionText || ''} onChange={e => setEditQ({ ...editQ, questionText: e.target.value })} rows={2} />
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Options (JSON)</label>
              <Textarea placeholder='[{"label":"Option A", "value":"A"}]' value={editQ.optionsJson || '[]'}
                onChange={e => setEditQ({ ...editQ, optionsJson: e.target.value })} rows={4} className="font-mono text-xs" />
            </div>
            <Input placeholder="Correct answer (value, e.g. A)" value={editQ.correctAnswer || ''} onChange={e => setEditQ({ ...editQ, correctAnswer: e.target.value })} />
            <Textarea placeholder="Explanation" value={editQ.explanation || ''} onChange={e => setEditQ({ ...editQ, explanation: e.target.value })} rows={2} />
            <div className="flex items-center gap-2">
              <Switch checked={editQ.isCritical || false} onCheckedChange={v => setEditQ({ ...editQ, isCritical: v })} />
              <label className="text-sm">Critical question (must be correct to pass)</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveQuestion} disabled={saving || !editQ.questionText}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteConfirm?.type}?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteConfirm?.label}" will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
